import {useLazyQuery} from "@apollo/client";
import get from "lodash/get";
import sortBy from "lodash/sortBy";
import {useFormContext} from "react-hook-form";

import {ContactsSelect} from "@/components/contactsSelect/ContactsSelect.tsx";
import {SelectField, SelectFieldBaseProps} from "@/components/form/fields/SelectField/SelectField.tsx";
import {useLoadOptions} from "@/lib/hooks/useLoadOptions.ts";
import {ContactGroupsBaseResult, GQL_CONTACT_GROUPS_BASIC_QUERY} from "@/lib/queries/contacts.ts";
import {ContactGroupBase, PENDING_CONTACT_ID} from "@/lib/types/contacts.ts";

interface ContactsSelectFieldProps extends SelectFieldBaseProps {
  contacts?: ContactGroupBase[];
  value?: number[];
  onChange?: (value: number[]) => void;
  shouldClearErrors?: boolean;
  creatable?: boolean;
  pendingContactsName?: string;
}

const toPendingContact = (name: string): ContactGroupBase => ({id: PENDING_CONTACT_ID, name});

export const ContactsSelectField = ({
  contacts: options,
  value: propsValue,
  onChange,
  name,
  creatable,
  pendingContactsName = "new_contact_groups",
  label = "Contacts",
  placeholder = "Select Contacts...",
  ...rest
}: ContactsSelectFieldProps) => {
  const {
    watch,
    setValue,
    trigger,
    formState: {defaultValues},
  } = useFormContext();
  const ids: number[] = propsValue ?? watch(name) ?? [];
  const pendingNames: string[] = (creatable ? watch(pendingContactsName) : undefined) ?? [];
  const defaultValue = get(defaultValues, name);

  const [getContacts] = useLazyQuery<ContactGroupsBaseResult>(GQL_CONTACT_GROUPS_BASIC_QUERY, {
    variables: {
      limit: 100,
      offset: 0,
      sort: "name",
    },
  });

  const {options: contacts, isLoading} = useLoadOptions<ContactGroupBase, ContactGroupsBaseResult, false>({
    options: options,
    fetchOptions: getContacts,
    transform: (result) => Array.from(result.data?.contactGroups?.items || []),
  });

  const commitIds = (selection: number[]) => {
    if (onChange) {
      onChange(selection);
    } else {
      setValue(name, selection, {shouldDirty: true, shouldValidate: true});
    }
  };

  const commitPendingNames = (selection: string[]) => {
    if (creatable) {
      setValue(pendingContactsName, selection, {shouldDirty: true});
      void trigger(name);
    }
  };

  return (
    <SelectField
      name={name}
      placeholder={placeholder}
      label={label}
      {...rest}
      select={
        <ContactsSelect
          isMulti={true}
          isLoading={isLoading}
          creatable={creatable}
          options={sortBy(contacts, (c) => c.name)}
          defaultValue={contacts ? contacts.filter((c) => c.id === defaultValue) : null}
          value={[...contacts.filter((c) => ids.includes(c.id)), ...pendingNames.map(toPendingContact)]}
          onChange={(selected: readonly ContactGroupBase[]) => {
            commitIds(selected.filter((c) => c.id !== PENDING_CONTACT_ID).map((c) => c.id));
            commitPendingNames(selected.filter((c) => c.id === PENDING_CONTACT_ID).map((c) => c.name));
          }}
          onCreateOption={(inputValue) => {
            const contactName = inputValue.trim();
            if (contactName && !pendingNames.includes(contactName)) {
              commitPendingNames([...pendingNames, contactName]);
            }
          }}
        />
      }
    />
  );
};
